import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, HelpCircle, XCircle, ExternalLink, Sparkles, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Sprint 1.5 Orchestration — Recommendations Panel
 * Composes /api/idip/funding/shortlist. Zero LLM. Zero recurring cost.
 * Replaces the "Which grant?" prompt with a ranked, verdict-labelled shortlist.
 */
export default function RecommendationsPanel({ staffId, onBeginDrafting, onOpenGrant }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/idip/funding/shortlist", { params: { limit: 10 } });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const beginDrafting = async (opp) => {
    setBusyId(opp.id);
    try {
      const { data: r } = await api.post(
        `/idip/funding/opportunity/${opp.id}/begin-drafting`,
        {},
        { params: { staff_id: staffId } }
      );
      toast.success("Imported to grant pipeline. Ready for analysis.");
      onBeginDrafting?.(r.grant_id);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const notNow = async (opp) => {
    setBusyId(opp.id);
    try {
      await api.post(`/idip/funding/opportunity/${opp.id}/not-now`);
      toast.success("Marked as reviewed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const verdictBadge = (v) => {
    if (v === "GO") return { icon: CheckCircle2, cls: "bg-forest/10 text-forest border-forest/30" };
    if (v === "REVIEW") return { icon: HelpCircle, cls: "bg-terracotta/10 text-terracotta border-terracotta/30" };
    return { icon: XCircle, cls: "bg-bone text-ink-soft border-line" };
  };

  if (loading && !data) return <div className="text-sm text-ink-soft italic p-4">Loading recommendations…</div>;
  if (!data) return null;

  const items = data.items || [];

  return (
    <div className="rounded-xl border border-line bg-admin-surface p-4" data-testid="recommendations-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-forest" strokeWidth={1.5} />
          <h3 className="font-serif text-lg text-ink">Today&rsquo;s Recommendations</h3>
          <Badge variant="outline" className="text-[9px] uppercase">Deterministic · Zero cost</Badge>
        </div>
        <div className="text-[10px] text-ink-soft">
          GO ≥ {data.thresholds?.go} · REVIEW ≥ {data.thresholds?.review}
        </div>
      </div>

      {data.profile_gaps?.length > 0 && (
        <div className="mb-3 p-2 text-xs bg-terracotta/5 border border-terracotta/20 rounded text-ink-soft">
          <strong className="text-terracotta">Profile readiness gaps:</strong> {data.profile_gaps.join(", ")}. Populate <a href="/admin/institutional-profile" className="underline text-forest">Institutional Profile</a> to raise fit scores.
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-ink-soft italic text-center py-6">
          No open opportunities. Discovery scheduler runs daily at 03:00 UTC.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="recommendations-list">
          {items.map(op => {
            const V = verdictBadge(op.verdict);
            const Icon = V.icon;
            const isOpen = expanded === op.id;
            return (
              <li key={op.id} className="border border-line rounded-lg overflow-hidden" data-testid={`rec-${op.id}`}>
                <div className="p-3">
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-14 h-14 rounded-lg border ${V.cls} flex flex-col items-center justify-center`}>
                      <Icon className="h-4 w-4" strokeWidth={1.5} />
                      <div className="text-[9px] uppercase tracking-wider mt-0.5">{op.verdict}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{op.title || "(untitled)"}</div>
                      <div className="text-xs text-ink-soft mt-0.5 flex flex-wrap gap-x-2">
                        {op.funder && <span>{op.funder}</span>}
                        {op.deadline && <><span>·</span><span>Due {op.deadline}</span></>}
                        {op.amount_max && <><span>·</span><span>${Number(op.amount_max).toLocaleString()}</span></>}
                        <span>·</span><span>fit {op.composite_score}/100</span>
                      </div>
                      <div className="text-xs text-ink-soft italic mt-1">{op.rationale}</div>
                      {op.readiness_gaps?.length > 0 && (
                        <div className="text-[10px] text-terracotta mt-1">
                          Gaps: {op.readiness_gaps.join(", ")}
                        </div>
                      )}
                    </div>
                    <button onClick={() => setExpanded(isOpen ? null : op.id)} className="text-ink-soft p-1">
                      {isOpen ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}
                    </button>
                  </div>
                  <div className="flex gap-2 mt-2 pl-[68px]">
                    {op.verdict === "GO" && (
                      <Button
                        size="sm"
                        onClick={() => beginDrafting(op)}
                        disabled={busyId === op.id}
                        className="bg-forest text-bone hover:bg-ink rounded-full h-7 text-xs"
                        data-testid={`begin-drafting-${op.id}`}
                      >
                        Begin Drafting
                      </Button>
                    )}
                    {op.verdict === "REVIEW" && op.url && (
                      <a href={op.url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center text-xs text-forest underline">
                        <ExternalLink className="h-3 w-3 mr-1"/> Review opportunity
                      </a>
                    )}
                    <Button
                      size="sm" variant="outline"
                      onClick={() => notNow(op)}
                      disabled={busyId === op.id}
                      className="rounded-full h-7 text-xs"
                      data-testid={`not-now-${op.id}`}
                    >
                      Not Now
                    </Button>
                  </div>
                </div>
                {isOpen && op.score_breakdown && (
                  <div className="border-t border-line bg-bone px-3 py-2">
                    <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Score breakdown</div>
                    <div className="grid gap-1 text-xs">
                      {Object.entries(op.score_breakdown).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[1fr,auto,3fr] gap-2">
                          <span className="capitalize">{k.replace(/_/g, " ")}</span>
                          <span className="font-mono text-ink-soft w-16 text-right">
                            {Math.round(v.score)} × {Math.round(v.weight * 100)}%
                          </span>
                          <span className="text-ink-soft italic truncate">{v.explanation}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
